import type { Asset, Country } from "../types/deal";

/**
 * Address fields whose changes trigger automatic geocoding on the asset form.
 * The asset name is deliberately excluded: renaming an asset should not move its pin
 * or spend a Geocoding API call.
 */
export const GEOCODE_TRIGGER_FIELDS = [
  "address_1",
  "address_2",
  "city",
  "postcode",
  "country",
] as const;

type GeocodableAsset = Partial<
  Pick<Asset, "address_1" | "address_2" | "city" | "postcode">
> & {
  // The country select stores a single Country, while API payloads may carry an array.
  country?: Country | Country[] | string | null;
};

const countryKey = (country: GeocodableAsset["country"]): string => {
  const value = Array.isArray(country) ? country[0] : country;
  if (!value) return "";
  if (typeof value === "string") return value.trim().toLowerCase();
  return String(value.id ?? value.nicename ?? value.name ?? "");
};

const textKey = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

/** A stable key for the address fields that drive automatic geocoding. */
export const getGeocodeAddressKey = (asset: GeocodableAsset): string =>
  JSON.stringify([
    textKey(asset.address_1),
    textKey(asset.address_2),
    textKey(asset.city),
    textKey(asset.postcode),
    countryKey(asset.country),
  ]);

/** Mirrors the minimum address information performGeocoding requires. */
export const hasGeocodableAddress = (asset: GeocodableAsset): boolean =>
  Boolean(
    textKey(asset.address_1) || textKey(asset.city) || textKey(asset.postcode),
  );

/**
 * Whether the asset form should automatically geocode: only when there is enough address
 * information and the address differs from the one last geocoded (or loaded).
 */
export const shouldAutoGeocode = (
  asset: GeocodableAsset,
  lastGeocodedKey: string | null,
): boolean =>
  hasGeocodableAddress(asset) &&
  getGeocodeAddressKey(asset) !== lastGeocodedKey;
